import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firePageEnrichment, rankActivityPage, fireActivityPage } from '../lib/claudeApi';
import { API_BASE } from '../lib/config';


const TRAVELER_PROFILES = [
  {
    name: 'The Pioneer',
    description: 'You consider a destination ruined the moment it appears on a top ten list. You want the place before the crowds find it. Discovery is not a preference, it is a requirement.',
  },
  {
    name: 'The Adventurer',
    description: 'You want to come home having seen something most people have never googled. Not just visited, but genuinely encountered. You use iconic destinations as orientation points, not destinations.',
  },
  {
    name: 'The Explorer',
    description: 'One anchor to orient yourself, everything else is discovery. You find the version of a place most visitors never reach because they did not know to look.',
  },
  {
    name: 'The Wanderluster',
    description: 'The destination almost does not matter. What matters is that it is somewhere new. You travel because you love to travel, full stop, and every new place is a good place.',
  },
  {
    name: 'The Curious Traveler',
    description: 'Half familiar, half discovery. You come home with stories and recommendations nobody else at the dinner table has heard. You balance comfort with genuine curiosity.',
  },
  {
    name: 'The Selective Explorer',
    description: 'You appreciate the classics but always find that one place nobody else has been to. You do the research, go off script at least once, and it is always the best part of the trip.',
  },
  {
    name: 'The Classics',
    description: 'You know what you love and you love it for good reason. The iconic destinations deliver and you are here for every bit of it. You do the classics properly, which most people never do.',
  },
  {
    name: 'The Foodie',
    description: 'You experience every destination through what it grows, cooks, and serves. Markets at dawn, hole-in-the-wall restaurants locals actually eat at, dishes you have never heard of that become the story you tell when you get home.',
  },
  {
    name: 'The Connoisseur',
    description: 'You travel partly for what a place produces. Vineyard visits, craft brewery tours, whisky distilleries, local spirits. Understanding what is made here and why is as important as where you eat or what you see.',
  },
  {
    name: 'The Curator',
    description: 'You move through a destination like someone who has done the research and knows exactly what deserves your time. Museums, historical sites, architecture, art. You seek the best version of every cultural experience.',
  },
  {
    name: 'The Wanderer',
    description: 'Water, mountains, forests, open landscapes. You feel most alive when nature is the backdrop. Whether you are hiking a ridge, sitting beside a river, or swimming in something cold and clear.',
  },
  {
    name: 'The Adventurer (Activity)',
    description: 'You want to come home having done something. A climb, a dive, a trail that required more than you expected. The physical challenge is the point, not the obstacle.',
  },
  {
    name: 'The Live Wire',
    description: 'You come alive after dark. Live music, the right bar on the right street, a city that does not start until midnight. Nightlife is not the end of the day for you, it is often the best part of it.',
  },
  {
    name: 'The Drifter',
    description: 'You do not need a plan to have a perfect trip. A good place, enough time, and the freedom to follow whatever feels right. The best moments find you rather than the other way around.',
  },
  {
    name: 'The Restorer',
    description: 'You travel to come back to yourself. Spas, slow mornings, quiet coastlines, a long lunch with nowhere to be after. The best trip leaves you genuinely rested.',
  },
  {
    name: 'The Immersionist',
    description: 'You want to be inside a place, not observing it. Food, neighborhoods, people, culture, daily life. You go deep across all of it rather than specializing in one dimension.',
  },
  {
    name: 'The Cultural Omnivore',
    description: 'History, food, art, local life. You consume a destination across every cultural dimension simultaneously. Culture is not one thing for you, it is everything.',
  },
  {
    name: 'The Maximalist',
    description: 'You want everything turned up. Adventure before lunch, a great meal after, live music at night, a hike tomorrow morning. You make the most of every activity, every environment, and every day.',
  },
];

if (typeof document !== 'undefined' && !document.getElementById('carpe-spinner-style')) {
  const style = document.createElement('style');
  style.id = 'carpe-spinner-style';
  style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

function Loading() {
  const navigate = useNavigate();

  // Read loading context once on mount — store in ref to persist across renders
  const contextRef = useRef(null);
  if (!contextRef.current) {
    contextRef.current = JSON.parse(sessionStorage.getItem('carpeLoadingContext') || '{}');
  }
  const context = contextRef.current;
  const {
    type,
    endpoint,
    payload,
    destination: destinationPage,
    loadingMessage = 'Finding your perfect match...',
  } = context;

  const [mode, setMode] = useState(context.startPassive ? 'passive' : 'questions');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [microAnswers, setMicroAnswers] = useState({});
  const [profileIndex, setProfileIndex] = useState(0);
  const [factIndex, setFactIndex] = useState(0);
  const [facts, setFacts] = useState([]);
  const [claudeDone, setClaudeDone] = useState(false);
  const [page1Ready, setPage1Ready] = useState(false);
  const [fading, setFading] = useState(false);
  const claudeResult = useRef(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [bgPhotos, setBgPhotos] = useState([]);
  const [bgPhotoIndex, setBgPhotoIndex] = useState(0);
  const [bgFading, setBgFading] = useState(false);

  // Fetch background photos - Phase 1
  useEffect(() => {
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;

    async function fetchPhotos() {
      try {
        let photos = [];
        if (type === 'activities' && payload?.destination) {
          const r = await fetch(
            `${SUPABASE_URL}/rest/v1/destination_photos?destination_name=eq.${encodeURIComponent(payload.destination)}&select=photo_urls`,
            { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } }
          );
          const data = await r.json();
          if (data[0]?.photo_urls?.length) {
            photos = data[0].photo_urls;
          }
        }
        if (photos.length < 5) {
          const r = await fetch(
            `${SUPABASE_URL}/rest/v1/destination_photos?select=photo_urls&limit=10&order=destination_name`,
            { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } }
          );
          const data = await r.json();
          const poolPhotos = data.flatMap(d => d.photo_urls || []);
          const shuffled = poolPhotos.sort(() => Math.random() - 0.5).slice(0, 20);
          photos = [...photos, ...shuffled];
        }
        setBgPhotos(photos.sort(() => Math.random() - 0.5).slice(0, 20));
      } catch {
        // Silent fail
      }
    }
    fetchPhotos();
  }, [type, payload]);

  // Fetch destination-specific photos - Phase 2 (after Claude returns)
  useEffect(() => {
    if (!claudeDone) return;
    if (type !== 'destinations' && type !== 'destinations-by-experience') return;
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;

    async function fetchDestinationPhotos() {
      try {
        const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
        const dests = pool['page1'] || claudeResult.current?.destinations || [];
        if (!dests.length) return;
        const names = dests.map(d => d.name);
        const nameFilter = names.map(n => `destination_name.eq.${encodeURIComponent(n)}`).join(',');
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/destination_photos?or=(${nameFilter})&select=photo_urls,destination_name`,
          { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } }
        );
        const data = await r.json();
        const photos = data.flatMap(d => d.photo_urls || []);
        if (photos.length >= 5) {
          setBgPhotos(photos.sort(() => Math.random() - 0.5).slice(0, 30));
          setBgPhotoIndex(0);
        }
      } catch {
        // Silent fail
      }
    }
    fetchDestinationPhotos();
  }, [claudeDone, type]);

  // Cycle background photos every 5 seconds
  useEffect(() => {
    if (bgPhotos.length < 2) return;
    const interval = setInterval(() => {
      setBgFading(true);
      setTimeout(() => {
        setBgPhotoIndex(prev => (prev + 1) % bgPhotos.length);
        setBgFading(false);
      }, 600);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgPhotos]);

// page1Ready is now set directly by firePageEnrichment's onComplete callback
// (no polling — eliminates race condition where button enables before data is written)

  useEffect(() => {
    async function loadQuestions() {
      const userId = sessionStorage.getItem('carpeUserId');
      let answeredIds = [];

      if (userId) {
        try {
          const res = await fetch(
            `${API_BASE}/auth/profile/${userId}`
          );
          const data = await res.json();
          if (data.profile) {
            const col = (type === 'activities')
              ? 'answered_dest_questions'
              : 'answered_activity_questions';
            answeredIds = data.profile[col] || [];
          }
        } catch {
          // Silent fail - use full bank
        }
      }

      // Fetch micro questions from backend
      // Use family bank when children_flag is active — family trips need
      // family-specific questions, not individual traveler profile questions
      let bank = [];
      try {
        const profile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
        const isFamily = profile.children_flag === true;
        let bankType;
        if (isFamily) {
          bankType = 'family';
        } else {
          bankType = (type === 'activities') ? 'destination' : 'activity';
        }
        const bankRes = await fetch(`${API_BASE}/quiz/micro-questions?type=${bankType}`);
        const bankData = await bankRes.json();
        bank = bankData.questions || [];
      } catch {
        bank = [];
      }

      // Filter out answered questions
      let available = bank.filter(q => !answeredIds.includes(q.id));

      // If all answered reset and use full bank
      if (available.length === 0) {
        available = [...bank];
      }

      // Shuffle
      for (let i = available.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [available[i], available[j]] = [available[j], available[i]];
      }

      setShuffledQuestions(available);
    }

    loadQuestions();
  }, [type]);

  const [shuffledProfiles] = useState(() => {
    const p = [...TRAVELER_PROFILES];
    for (let i = p.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [p[i], p[j]] = [p[j], p[i]];
    }
    return p;
  });

  useEffect(() => {
    if (!destinationPage) {
      navigate('/');
      return;
    }

    // Returning user path — skip Claude call, fire enrichment for saved destinations
    if (type === 'returning') {
      fetch(`${API_BASE}/facts/random?count=8`)
        .then(res => res.json())
        .then(data => setFacts(data.facts || []))
        .catch(() => {});
      const savedDests = JSON.parse(sessionStorage.getItem('carpeSavedDestinations') || '[]');
      const bgProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');

      // Skip enrichment if teasers already in sessionStorage — Login.jsx hydrated them
      // from Supabase on sign-in so no Claude calls needed on return visits
      const existingTeasers = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
      const teasersAlreadyLoaded = Object.keys(existingTeasers).length > 0;

      if (savedDests.length > 0 && !teasersAlreadyLoaded) {
        // Fire enrichment A — 5x1 concurrent teaser calls
        savedDests.forEach(dest => {
          fetch(`${API_BASE}/recommendations/destinations/enrich-teasers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile: bgProfile, destinations: [dest], user_id: sessionStorage.getItem('carpeUserId') }),
          })
            .then(res => res.json())
            .then(enrichData => {
              const enriched = enrichData.enriched || [];
              if (enriched.length > 0) {
                const current = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
                current[enriched[0].name] = enriched[0].teaser_activities || [];
                sessionStorage.setItem('carpeEnrichTeasers', JSON.stringify(current));
              }
            })
            .catch(() => {});
        });

        // Fire enrichment B — 5x1 concurrent facts calls
        savedDests.forEach(dest => {
          fetch(`${API_BASE}/recommendations/destinations/enrich-facts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile: bgProfile, destinations: [dest], user_id: sessionStorage.getItem('carpeUserId') }),
          })
            .then(res => res.json())
            .then(enrichData => {
              const enriched = enrichData.enriched || [];
              if (enriched.length > 0) {
                const current = JSON.parse(sessionStorage.getItem('carpeEnrichFacts') || '{}');
                current[enriched[0].name] = {
                  profile_match_facts: enriched[0].profile_match_facts || [],
                  profile_surprise_facts: enriched[0].profile_surprise_facts || [],
                };
                sessionStorage.setItem('carpeEnrichFacts', JSON.stringify(current));
              }
            })
            .catch(() => {});
        });
      }

      // Proceed immediately — enrichment loads in background while user sees passive screen
      claudeResult.current = { destinations: savedDests };
      setClaudeDone(true);
      return;
    }

    if (!endpoint) {
      navigate('/');
      return;
    }

    // Use destination-specific facts if available, otherwise fetch generic facts
    const destinationFacts = context.destinationFacts || [];
    if (destinationFacts.length > 0) {
      setFacts(destinationFacts.map(f => ({ fact: f })));
    } else {
      fetch(`${API_BASE}/facts/random?count=8`)
        .then(res => res.json())
        .then(data => setFacts(data.facts || []))
        .catch(() => {});
    }

    // Fire Claude call
    const userId = sessionStorage.getItem('carpeUserId');
    const payloadWithUser = userId ? { ...payload, user_id: userId } : payload;
    // Activities type: use new rank+enrich pipeline via fireActivityPage.
    // Loading.jsx owns the wait — Activities.jsx reads from carpeActivityPool and renders immediately.
    if (type === 'activities') {
      const destName = payload?.destination;
      const destCountry = payload?.country;
      const profileData = payload?.profile || payload;
      const eventContext = (payload?.lat && payload?.lng && payload?.event_date)
        ? { lat: payload.lat, lng: payload.lng, event_date: payload.event_date }
        : null;
      const activityCacheKey = eventContext ? `${destName}::${eventContext.event_date}` : destName;
      const stateProvince = payload?.state_province || null;
      console.log('[EVENTS DEBUG] payload:', payload);
      console.log('[EVENTS DEBUG] eventContext:', eventContext);
      // Rank first (~1.8s) — gives us page 1 names immediately so page 1
      // and page 2 enrichment can fire truly concurrently, not sequentially.
      rankActivityPage(destName, destCountry, profileData, stateProvince, eventContext).then(page1Names => {
        const page1ExcludeForPage2 = page1Names.map(a => a.name);

        // Fire page 1 enrich (named activities) — passes preRankedActivities
        // so fireActivityPage skips its internal rank step entirely
        fireActivityPage(
          destName, destCountry, profileData, 1, [],
          (page1Activities) => {
            claudeResult.current = { activities: page1Activities };
            setClaudeDone(true);
          },
          page1Names,  // preRankedActivities — page 1 skips rank, goes straight to enrich
          eventContext,
          stateProvince
        );

        // Fire page 2 at the exact same time as page 1 — not waiting for page 1 to finish.
        // Uses page 1's ranked names as exclusions — no overlap possible.
        fireActivityPage(
          destName, destCountry, profileData, 2, page1ExcludeForPage2,
          (page2Activities) => {
            // Page 3 fires the instant page 2 resolves
            const exclusions = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
            const combined = exclusions[activityCacheKey] || [];
            fireActivityPage(
              destName, destCountry, profileData, 3, combined,
              (page3Activities) => {
                // Page 4 fires the instant page 3 resolves
                const exclusions2 = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
                const combined2 = exclusions2[activityCacheKey] || [];
                fireActivityPage(destName, destCountry, profileData, 4, combined2, () => {}, null, eventContext, stateProvince);
              },
              null, eventContext, stateProvince
            );
          },
          null, eventContext, stateProvince
        );
      }).catch(() => {
        setClaudeDone(true);
      });
      return;
    }

    fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payloadWithUser),
    })
      .then(res => res.json())
      .then(data => {
        claudeResult.current = data;
        if (type === 'destinations' || type === 'destinations-by-experience') {
          const allRanked = data.destinations || [];
          const userId = sessionStorage.getItem('carpeUserId');
          sessionStorage.setItem('carpeRankedPool', JSON.stringify(allRanked));
          firePageEnrichment(allRanked.slice(0, 5), 1, payload, userId, () => {
            setPage1Ready(true);
            firePageEnrichment(allRanked.slice(5, 10), 2, payload, userId, null);
          });
        }
        setClaudeDone(true);
      })
      .catch(() => {
        setClaudeDone(true);
      });
      // eslint-disable-next-line 
  }, [endpoint, destinationPage, navigate]);

  // Rotate profiles in passive mode
  useEffect(() => {
    if (mode !== 'passive') return;
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setProfileIndex(prev => (prev + 1) % shuffledProfiles.length);
        setFactIndex(prev => (prev + 1) % Math.max(facts.length, 1));
        setFading(false);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, [mode, facts.length, shuffledProfiles.length]);

  function handleSelectAnswer(letter) {
    const currentQ = shuffledQuestions[questionIndex];
    if (currentQ.multi_select) {
      const max = currentQ.max_selections || 2;
      const current = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      if (current.includes(letter)) {
        setSelectedAnswer(current.filter(l => l !== letter));
      } else if (current.length < max) {
        setSelectedAnswer([...current, letter]);
      }
    } else {
      setSelectedAnswer(letter);
    }
  }

  function isSelected(letter) {
    if (!selectedAnswer) return false;
    if (Array.isArray(selectedAnswer)) return selectedAnswer.includes(letter);
    return selectedAnswer === letter;
  }

  function hasAnswer() {
    if (!selectedAnswer) return false;
    if (Array.isArray(selectedAnswer)) return selectedAnswer.length > 0;
    return true;
  }

  async function saveAnsweredQuestion(questionId) {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) return;
    
    const column = (type === 'activities') 
      ? 'answered_dest_questions' 
      : 'answered_activity_questions';

    try {
      await fetch(`${API_BASE}/auth/profile/answered-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          column: column,
          question_id: questionId,
        }),
      });
    } catch {
      // Silent fail - not a blocker
    }
  }

function handleNext() {
    if (!hasAnswer()) return;

    const currentQ = shuffledQuestions[questionIndex];
    const updatedAnswers = { ...microAnswers, [currentQ.id]: selectedAnswer };
    setMicroAnswers(updatedAnswers);
    sessionStorage.setItem('carpeMicroAnswers', JSON.stringify(updatedAnswers));

    // Track this question as answered for this user
    saveAnsweredQuestion(currentQ.id);

    const isReady = (type === 'destinations' || type === 'destinations-by-experience') ? page1Ready : claudeDone;
    if (isReady) {
      handleProceed();
      return;
    }

    if (questionIndex < shuffledQuestions.length - 1) {
      setSelectedAnswer(null);
      setQuestionIndex(prev => prev + 1);
    } else {
      setMode('passive');
    }
  }  

  function handleSkip() {
    setMode('passive');
  }

  function handleProceed() {
    if (!claudeResult.current) return;
    const data = claudeResult.current; // eslint-disable-line no-unused-vars

    // Returning user path — destinations already in sessionStorage, just navigate
    if (type === 'returning') {
      sessionStorage.removeItem('carpeLoadingContext');
      navigate(destinationPage);
      return;
    }

    // Store results based on type
    if (type === 'destinations' || type === 'destinations-by-experience') {
      const userId = sessionStorage.getItem('carpeUserId');
      const wasReturning = sessionStorage.getItem('carpeWasReturning');
      // page1 enrichment fired in .then(data=>) — carpeSavedDestinations already set
      // Read page1 from pool for facts and save calls
      const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
      const page1Dests = pool['page1'] || JSON.parse(sessionStorage.getItem('carpeSavedDestinations') || '[]');
      // Fire enrich-act-loading-facts for page 1 (non-blocking)
      fetch(`${API_BASE}/recommendations/destinations/enrich-act-loading-facts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile: payload,
          destinations: page1Dests,
          user_id: userId,
        }),
      })
        .then(res => res.json())
        .then(enrichData => {
          const enriched = enrichData.enriched || [];
          const current = JSON.parse(sessionStorage.getItem('carpeEnrichFacts') || '{}');
          enriched.forEach(dest => {
            current[dest.name] = {
              profile_match_facts: dest.profile_match_facts || [],
              profile_surprise_facts: dest.profile_surprise_facts || [],
            };
          });
          sessionStorage.setItem('carpeEnrichFacts', JSON.stringify(current));
          // Patch Supabase with facts now that they've arrived —
          // the initial save fired before facts resolved so enrich_facts was empty
          if (userId && !wasReturning) {
            const saveId = sessionStorage.getItem('carpeCurrentSaveId');
            fetch(`${API_BASE}/auth/destinations/update-facts`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                enrich_facts: current,
                row_id: saveId,
              }),
            }).catch(() => {});
          }
        })
        .catch(() => {});

      // Save to Supabase.
      // destinations = enriched page 1 (5, with match_reason) + raw ranked 6-30
      // so returning users get page 1 fully enriched and pages 2-6 re-enrich on demand.
      if (userId && !wasReturning) {
        const finalTeasers = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
        const allRanked = JSON.parse(sessionStorage.getItem('carpeRankedPool') || '[]');
        const enrichedPool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
        const enrichedPage1 = enrichedPool['page1'] || page1Dests;
        // First 5 = enriched (match_reason included), rest = raw ranked
        const destinationsToSave = [
          ...enrichedPage1,
          ...allRanked.slice(5),
        ];
        fetch(`${API_BASE}/auth/destinations/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            destinations: destinationsToSave.length > 0 ? destinationsToSave : page1Dests,
            reshuffle_pool: [],
            profile_snapshot: payload,
            enrich_teasers: finalTeasers,
            enrich_facts: {},
          }),
        })
          .then(res => res.json())
          .then(saveData => {
            if (saveData.id) {
              sessionStorage.setItem('carpeCurrentSaveId', saveData.id);
            }
          })
          .catch(() => {});
      }
    }

    sessionStorage.removeItem('carpeLoadingContext');
    // carpeSavedDestinations is set by firePageEnrichment when page1 is fully ready
    // page1Ready only fires after carpeSavedDestinations is set — so this is always populated
    const navDests = JSON.parse(sessionStorage.getItem('carpeSavedDestinations') || '[]');
    navigate(destinationPage, { state: { destinations: navDests } });
  }
  
  if (shuffledQuestions.length === 0) {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>Carpe Wonder™</div>
        {page1Ready && (
          <button style={styles.seeResultsBtn} onClick={handleProceed}>
            Seize your destinations →
          </button>
        )}
        {!page1Ready && (
          <div style={styles.waitingText}>{loadingMessage}</div>
        )}
      </div>
    );
  }

  const currentQ = shuffledQuestions[questionIndex];
  const currentProfile = shuffledProfiles[profileIndex];
  const currentFact = facts[factIndex];
  // Temporarily disabled — TripAdvisor source photos are unfiltered and
  // frequently irrelevant (cars, food, selfies instead of landmarks/scenery).
  // Re-enable once the hybrid photo-sourcing waterfall (Roadmap) is built.
  const PHOTOS_ENABLED = false;
  return (
    <div style={{
      ...styles.page,
      backgroundImage: (PHOTOS_ENABLED && bgPhotos[bgPhotoIndex]) ? `url(${bgPhotos[bgPhotoIndex]})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      opacity: bgFading ? 0.85 : 1,
      transition: 'opacity 0.6s ease',
      position: 'relative',
    }}>
      {bgPhotos[bgPhotoIndex] && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(13,13,31,0.82)',
          zIndex: 0,
          pointerEvents: 'none',
        }} />
      )}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={styles.logo}>Carpe Wonder™</div>

      {mode === 'questions' ? (
        <div style={styles.questionWrap}>
          <div style={styles.questionLabel}>While you wait</div>
          <div style={styles.loadingSubtext}>{loadingMessage}</div>
          <div style={styles.questionText}>{currentQ.text}</div>

          {currentQ.multi_select && (
            <div style={styles.multiHint}>Select up to {currentQ.max_selections}</div>
          )}

          <div style={styles.optionsList}>
            {Object.entries(currentQ.options).map(([letter, text]) => {
              const selected = isSelected(letter);
              return (
                <button
                  key={letter}
                  style={{
                    ...styles.option,
                    borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
                    background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)',
                    color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.75)',
                  }}
                  onClick={() => handleSelectAnswer(letter)}
                >
                  <span style={styles.optionLetter}>{letter.toUpperCase()}</span>
                  <span style={styles.optionText}>{text}</span>
                </button>
              );
            })}
          </div>

          <div style={styles.loadingActions}>
            <button
              style={{
                ...styles.nextBtn,
                opacity: (hasAnswer() || (type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone)) ? 1 : 0.35,
                cursor: (hasAnswer() || (type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone)) ? 'pointer' : 'not-allowed',
                background: (type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone) ? 'var(--gold)' : 'transparent',
                color: (type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone) ? '#0d0d1f' : 'var(--gold)',
                border: (type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone) ? 'none' : '1px solid var(--gold)',
              }}
              onClick={(type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone) ? handleProceed : handleNext}
              disabled={!hasAnswer() && !(type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone)}
            >
              {(type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone)
                ? type === 'activities'
                  ? 'Seize your activities →'
                  : 'Seize your destinations →'
                : 'Next →'}
            </button>
            <button style={styles.skipBtn} onClick={handleSkip}>
              Skip questions
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.passiveWrap}>
          <div style={styles.passiveNav}>
            <button style={styles.navBtn} onClick={() => {
              setFading(true);
              setTimeout(() => {
                setProfileIndex(prev => (prev - 1 + shuffledProfiles.length) % shuffledProfiles.length);
                setFactIndex(prev => (prev - 1 + Math.max(facts.length, 1)) % Math.max(facts.length, 1));
                setFading(false);
              }, 400);
            }}>←</button>

            <div style={styles.passiveContent}>
              <div style={{
                ...styles.profileCard,
                opacity: fading ? 0 : 1,
                transition: 'opacity 0.4s ease',
              }}>
                <div style={styles.passiveLabel}>Are you...</div>
                <div style={styles.passiveProfileName}>{currentProfile.name}?</div>
                <div style={styles.passiveProfileDesc}>{currentProfile.description}</div>
              </div>

              {facts.length > 0 && currentFact && (
                <div style={{
                  ...styles.factCard,
                  opacity: fading ? 0 : 1,
                  transition: 'opacity 0.4s ease',
                }}>
                  <div style={styles.factLabel}>Did you know?</div>
                  <div style={styles.factText}>{currentFact.fact}</div>
                </div>
              )}
            </div>

            <button style={styles.navBtn} onClick={() => {
              setFading(true);
              setTimeout(() => {
                setProfileIndex(prev => (prev + 1) % shuffledProfiles.length);
                setFactIndex(prev => (prev + 1) % Math.max(facts.length, 1));
                setFading(false);
              }, 400);
            }}>→</button>
          </div>

          {(type === 'destinations' || type === 'destinations-by-experience' ? page1Ready : claudeDone) ? (
            <button style={styles.seeResultsBtn} onClick={handleProceed}>
              {type === 'activities'
                ? 'Seize your activities →'
                : 'Seize your destinations →'}
            </button>
          ) : (
            <div style={styles.waitingText}>{loadingMessage}</div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 20px 48px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '32px',
    alignSelf: 'flex-start',
  },
  loadingSubtext: {
    fontSize: '13px',
    color: 'rgba(201,168,76,0.7)',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  questionWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  questionLabel: {
    fontSize: '12px',
    color: 'rgba(201,168,76,0.8)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  questionText: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#fff',
    lineHeight: '1.4',
    marginBottom: '8px',
  },
  multiHint: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '16px',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '24px',
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    border: '1px solid',
    borderRadius: '12px',
    padding: '14px 16px',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    width: '100%',
    cursor: 'pointer',
    minHeight: '44px',
  },
  optionLetter: {
    fontSize: '13px',
    fontWeight: '600',
    minWidth: '20px',
    marginTop: '1px',
    opacity: 0.7,
  },
  optionText: {
    fontSize: '14px',
    lineHeight: '1.5',
  },
  loadingActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
  },
  nextBtn: {
    width: '100%',
    border: '1px solid var(--gold)',
    borderRadius: '28px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '500',
    letterSpacing: '0.02em',
    transition: 'all 0.2s ease',
    minHeight: '44px',
  },
  skipBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px',
    minHeight: '44px',
  },
  passiveWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  passiveNav: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    width: '100%',
  },
  passiveContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  navBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '18px',
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileCard: {
    border: '0.5px solid rgba(201,168,76,0.25)',
    borderRadius: '14px',
    padding: '20px',
    background: 'rgba(201,168,76,0.04)',
    textAlign: 'center',
  },
  passiveLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  passiveProfileName: {
    fontSize: '20px',
    fontWeight: '500',
    color: 'var(--gold)',
    marginBottom: '10px',
  },
  passiveProfileDesc: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: '1.7',
  },
  factCard: {
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    padding: '16px 20px',
    background: 'rgba(255,255,255,0.03)',
  },
  factLabel: {
    fontSize: '12px',
    color: 'rgba(201,168,76,0.8)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  factText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: '1.6',
  },
  waitingText: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    letterSpacing: '0.05em',
  },
  seeResultsBtn: {
    width: '100%',
    background: 'var(--gold)',
    color: '#0d0d1f',
    border: 'none',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '44px',
  },
};

export default Loading;